import React, { useState } from 'react';
import './SettingsCards.css';

interface SettingsCardsProps {
  className?: string;
}

export const SettingsCards: React.FC<SettingsCardsProps> = ({ className }) => {
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [settings, setSettings] = useState({
    currency: 'USD',
    theme: 'Dark',
    animations: true,
    emailNotifications: true,
    pushNotifications: false,
    tradeAlerts: true,
    autoConfirm: false
  });

  const handleToggle = (key: keyof typeof settings) => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const toggleCard = (cardId: string) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  return (
    <div className={`settings-cards ${className || ''}`}>
      {/* Display Preferences Card */}
      <div className="settings-card">
        <div 
          className="card-header"
          onClick={() => toggleCard('display')}
        >
          <h3 className="card-title">Display Preferences</h3>
          <div className={`expand-icon ${expandedCard === 'display' ? 'expanded' : ''}`}>
            ▼
          </div>
        </div>
        {expandedCard === 'display' && (
          <div className="card-content">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Currency Display</div>
                <div className="setting-description">Choose your preferred currency format</div>
              </div>
              <div className="custom-dropdown">
                <select 
                  value={settings.currency} 
                  onChange={(e) => setSettings(prev => ({ ...prev, currency: e.target.value }))}
                  className="dropdown-select"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Theme</div>
                <div className="setting-description">Select your preferred theme</div>
              </div>
              <div className="custom-dropdown">
                <select 
                  value={settings.theme} 
                  onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
                  className="dropdown-select"
                >
                  <option value="Dark">Dark</option>
                  <option value="Light">Light</option>
                  <option value="Auto">Auto</option>
                </select>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Show Animations</div>
                <div className="setting-description">Enable smooth transitions and effects</div>
              </div>
              <div 
                className={`custom-toggle ${settings.animations ? 'active' : ''}`}
                onClick={() => handleToggle('animations')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Notifications Card */}
      <div className="settings-card">
        <div 
          className="card-header"
          onClick={() => toggleCard('notifications')}
        >
          <h3 className="card-title">Notifications</h3>
          <div className={`expand-icon ${expandedCard === 'notifications' ? 'expanded' : ''}`}>
            ▼
          </div>
        </div>
        {expandedCard === 'notifications' && (
          <div className="card-content">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Email Notifications</div>
                <div className="setting-description">Receive updates via email</div>
              </div>
              <div 
                className={`custom-toggle ${settings.emailNotifications ? 'active' : ''}`}
                onClick={() => handleToggle('emailNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Push Notifications</div>
                <div className="setting-description">Get notifications on your device</div>
              </div>
              <div 
                className={`custom-toggle ${settings.pushNotifications ? 'active' : ''}`}
                onClick={() => handleToggle('pushNotifications')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Trade Alerts</div>
                <div className="setting-description">Alerts for trade executions and updates</div>
              </div>
              <div 
                className={`custom-toggle ${settings.tradeAlerts ? 'active' : ''}`}
                onClick={() => handleToggle('tradeAlerts')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Trading Defaults Card */}
      <div className="settings-card">
        <div 
          className="card-header"
          onClick={() => toggleCard('trading')}
        >
          <h3 className="card-title">Trading Defaults</h3>
          <div className={`expand-icon ${expandedCard === 'trading' ? 'expanded' : ''}`}>
            ▼
          </div>
        </div>
        {expandedCard === 'trading' && (
          <div className="card-content">
            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Default Stake</div>
                <div className="setting-description">Your preferred stake amount</div>
              </div>
              <div className="stake-display">$10.00</div>
            </div>

            <div className="setting-item">
              <div className="setting-info">
                <div className="setting-label">Auto-confirm trades</div>
                <div className="setting-description">Skip confirmation dialogs</div>
              </div>
              <div 
                className={`custom-toggle ${settings.autoConfirm ? 'active' : ''}`}
                onClick={() => handleToggle('autoConfirm')}
              >
                <div className="toggle-slider"></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
